import { ColumnDef, SortingState } from "@tanstack/react-table";
import { format } from "date-fns";
import { ReactNode, useMemo } from "react";

import { PaymentsInvoice } from "~/api/Api";

import Button from "../mantine/button/Button";
import { getPrice } from "../pricing/utils";
import DataTable, { ActionColumn } from "../ui/dataTable/DataTable";
import styles from "./InvoicesTable.module.scss";

const DATE_FORMAT = "dd.MM.yyyy";

interface InvoicesTableProps {
  invoices: PaymentsInvoice[];
  getInvoiceStripeUrl: (id: number) => void;
  downloadInvoice: (id: number) => void;
  isLoadingDownloadInvoice: boolean;
  isLoadingGetInvoiceStripeUrl: boolean;
  isLoading: boolean;
  isFetching: boolean;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  toolbarActions?: ReactNode;
}

const getStatusClass = (status: string) => {
  switch (status) {
    case "paid":
      return styles.statusGreen;
    case "failed":
      return styles.statusRed;
    default:
      return styles.statusBlue;
  }
};

export const InvoicesTable = ({
  invoices,
  getInvoiceStripeUrl,
  downloadInvoice,
  isLoadingDownloadInvoice,
  isLoadingGetInvoiceStripeUrl,
  isLoading,
  isFetching,
  sorting,
  onSortingChange,
  searchValue,
  onSearchChange,
  toolbarActions,
}: InvoicesTableProps) => {
  const columns = useMemo<ColumnDef<PaymentsInvoice>[]>(
    () => [
      {
        accessorKey: "number",
        header: "Invoice ID",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) => {
          const status = info.getValue() as string;

          return (
            <span className={`${styles.badge} ${getStatusClass(status)}`}>
              {status}
            </span>
          );
        },
      },
      {
        accessorKey: "total",
        header: "Amount",
        cell: (info) => {
          const row = info.row.original;

          return getPrice(
            Number(row.total),
            String((row as Record<string, unknown>).currency ?? "USD"),
          );
        },
      },
      {
        accessorKey: "created_at",
        header: "Issue Date",
        cell: (info) => {
          const value = info.getValue() as string | undefined;

          return value ? format(new Date(value), DATE_FORMAT) : "";
        },
      },
      {
        accessorKey: "period_end",
        header: "Due Date",
        cell: (info) => {
          const value = info.getValue() as string | undefined;

          return value ? format(new Date(value), DATE_FORMAT) : "";
        },
      },
      {
        id: "actions",
        header: "",
        size: 50,
        enableSorting: false,
        cell: (info) => {
          const rowId = Number(info.row.original.id);

          return (
            <ActionColumn>
              {(close) => (
                <>
                  <Button
                    variant="menuItem"
                    onClick={() => {
                      close();
                      downloadInvoice(rowId);
                    }}
                    loading={isLoadingDownloadInvoice}
                  >
                    Download Invoice
                  </Button>
                  <Button
                    variant="menuItem"
                    onClick={() => {
                      close();
                      getInvoiceStripeUrl(rowId);
                    }}
                    loading={isLoadingGetInvoiceStripeUrl}
                  >
                    View on Stripe
                  </Button>
                </>
              )}
            </ActionColumn>
          );
        },
      },
    ],
    [
      downloadInvoice,
      getInvoiceStripeUrl,
      isLoadingDownloadInvoice,
      isLoadingGetInvoiceStripeUrl,
    ],
  );

  return (
    <DataTable
      columns={columns}
      data={invoices}
      isLoading={isLoading}
      isRefetching={isFetching}
      tableLayout="auto"
      externalSorting={{
        state: sorting,
        onChange: onSortingChange,
      }}
      search={{
        value: searchValue,
        onChange: onSearchChange,
        placeholder: "Search by invoice number...",
      }}
      toolbarActions={toolbarActions}
    />
  );
};
